import { useState, useCallback, useEffect } from 'react';
import IncidentTable from '../components/Table/IncidentTable';
import FilterButton from '../../../../components/_shared/FilterButton/FilterButton';
import IncidentFilteredModal from '../components/IncidentFilteredModal/IncidentFilteredModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getAllFilteredIncidents } from '../../../../../utils/api/incidentsAPI';
import APIErrorComponent from '../../../../components/_shared/Error/APIErrorComponent';
import { useDispatch, useSelector } from 'react-redux';
import CustomModuleHeading from '../../../../components/_shared/CustomModuleHeading/CustomModuleHeading';
import CustomizedErrorBoundary from '../../../../components/_shared/CustomizedErrorBoundary/CustomizedErrorBoundary';
import RotatingLinesLoader from '../../../../components/_shared/Loaders/RotatingLinesLoader';
import tableOptionInitialState from '../../../../../utils/services/tableOptionsInitialState';
import crossTabIncidents from '../../../../../utils/crossTabIncidents';
import PaginationAdjustor from '../../../../components/_shared/PaginationAdjustor/PaginationAdjustor';
import { fetchStateThunk } from './_redux/fetchStateThunk';
import { validatePermission } from '../../../../../utils/services/validatePermission';

const likes = ['incidentname_tx', 'description_tx', 'user_name_tx', 'device_name_tx', 'wmrc_division'];

const processEventsFieldData = (startValue, endValue) => {
    if ((!startValue && startValue !== 0) && (!endValue && endValue !== 0)) {
        return undefined;
    }
    let value;
    if (endValue !== '' && endValue !== undefined && endValue !== null) {
        value = `<=::${endValue}`;
    }
    if (startValue !== '' && startValue !== undefined && startValue !== null) {
        if (endValue !== '' && endValue !== undefined && endValue !== null) {
            value = `<=::${endValue}||>=::${startValue}`;
        } else {
            value = `>=::${startValue}`;
        }
    }
    return value;
};

const MyIncidentsListing = () => {
    let refStatusOption;
    const navigate = useNavigate();
    const filteredLabel = {
        status: 'Status ',
        startincident_dt: 'Start Date',
        endincident_dt: 'End Date',
        incidentname_tx: 'Incident Name',
        description_tx: 'Description',
        incident_id: 'Incident ID',
        device_cd: 'Device',
        startscore_nr: 'Score From',
        endscore_nr: 'Score To',
    };
    const queryClient = useQueryClient();
    const [sort, setSort] = useState(null);
    const { permissions } = useSelector((state) => state?.auth)

    const [pills, setPills] = useState({ status: 'NOT CLOSED' });
    const [clearBtn, setClearBtn] = useState(false);
    const [tableOptions, setTableOptions] = useState(tableOptionInitialState);
    const [isOpen, setIsOpen] = useState(false);
    const [filteredData, setFilteredData] = useState({ status: 'Not Closed' });
    const [columnFilters, setColumnFilters] = useState({});

    const handleColumnFilterChange = (key, value) => {
        setColumnFilters((prev) => {
            const updated = { ...prev };
            if (value === '' || value == null || value === undefined) delete updated[key];
            else updated[key] = value;
            return updated;
        });
        setTableOptions((prev) => ({ ...prev, activePage: 0 }));
    };

    const { user: loggedInUser } = useSelector((state) => state?.auth);
    const dispatch = useDispatch();
    const { isLoading: isLoadingRefStatus, isFetched: isFetchedRefStatus, getAllRefStatus: refStatus } = useSelector((state) => state?.myIncidentsFilter);
    if (isFetchedRefStatus) {
        refStatusOption = [
            ...refStatus
                .filter(

                    (item) =>
                        item.label !== 'UNREVIEWED' ||
                        validatePermission(permissions, 'myincidents', 'unreviewed_incidents_read')
                ),
        ]
    }
    // A combine done in another tab can't reach this tab's QueryClient.
    useEffect(() => {
        return crossTabIncidents.onIncidentsChanged(() => {
            queryClient.invalidateQueries({ queryKey: ['my-incidents'] });
        });
    }, [queryClient]);

    // Load status options for both modal + table header dropdown
    useEffect(() => {
        if (!isLoadingRefStatus && (!refStatus || refStatus.length === 0)) {
            dispatch(fetchStateThunk());
        }
    }, [isLoadingRefStatus, refStatus, dispatch]);

    const handlefilteredData = (data) => {
        const { _deviceLabel, ...rest } = data
        let newFilteredData = rest
        let pillsData = { ...rest }

        if (rest?.status) {
            const status = refStatus?.find((item) => item?.label === rest?.status)
            pillsData.status = status?.label
            newFilteredData = { ...rest, status: status?.value }
        }
        if (rest?.device_cd) {
            pillsData.device_cd = _deviceLabel ?? rest.device_cd
        }
        setPills(pillsData);
        setFilteredData(newFilteredData);
        setSort(null);
        setTableOptions((prev) => ({ ...prev, activePage: 0 }));
        queryClient?.invalidateQueries({
            queryKey: ['my-incidents', tableOptions, sort, filteredData],
        });
        let fixedFilterKeys = ['status', 'status_exit'];
        let isClearBtn = false;
        Object.keys(rest)?.forEach((ele) => {
            if (!fixedFilterKeys?.includes(ele)) {
                isClearBtn = true;
            }
        });
        setClearBtn(isClearBtn);
    };

    const handleOpenModal = () => {
        setIsOpen(true);
    };

    const handlModalClose = () => {
        setIsOpen(false);
    };

    const handleClearAllFilteredData = () => {
        const status = filteredData?.status;
        const statusPill = pills?.status;
        setFilteredData({ status });
        setColumnFilters({});
        setPills({ status: statusPill });
        setClearBtn(false);
        setTableOptions((prev) => ({ ...prev, activePage: 0 }));
        queryClient.invalidateQueries({
            queryKey: ['my-incidents', tableOptions, sort, filteredData],
        });
    };

    const handleRowsPerPage = (e) => {
        setTableOptions((prev) => ({
            ...prev,
            rowPerPages: e?.target?.value,
            activePage: 0,
        }));
    };

    const handlePageClick = (event) => {
        setTableOptions((prev) => ({ ...prev, activePage: event?.selected }));
    };

    const handleColumnSort = async (column, sortDirection) => {
        setSort((prev) => ({
            ...prev,
            columnName: column?.sortField,
            order: sortDirection,
        }));
        queryClient.invalidateQueries({
            queryKey: ['my-incidents', tableOptions, sort, filteredData],
        });
    };

    const getIncidents = useCallback(async () => {
        let apiQueryParams = {
            limit: tableOptions?.rowPerPages,
            offset: tableOptions?.activePage * tableOptions?.rowPerPages,
            count: true,
        };
        if (sort && sort?.columnName && sort?.order) {
            apiQueryParams = {
                ...apiQueryParams,
                order: sort?.order,
                sort: sort?.columnName,
            };
        }
        const mergedFilters = { ...filteredData };
        Object.entries(columnFilters).forEach(([key, value]) => {
            if (value != null && String(value).trim()) {
                mergedFilters[key] = value;
            }
        });
        const data = { ...mergedFilters };
        likes.forEach((ele) => {
            if (data.hasOwnProperty(ele)) {
                if (data[ele]?.slice(0, 4) !== 'like') {
                    data[ele] = `like:${data[ele]}`;
                }
            }
        });
        const score_nr = processEventsFieldData(filteredData?.startscore_nr, filteredData?.endscore_nr);
        if (score_nr) {
            delete data.startscore_nr;
            delete data.endscore_nr;
            data.score_nr = score_nr;
        }
        return getAllFilteredIncidents({
            ...apiQueryParams,
            ...data,
            user_id: loggedInUser?.user_id,
        });
    }, [
        tableOptions?.rowPerPages,
        tableOptions?.activePage,
        sort,
        filteredData,
        columnFilters,
        loggedInUser?.user_id,
    ]);

    const {
        isLoading,
        isFetching,
        data: incidents,
        isError,
        error,
        isSuccess,
    } = useQuery(
        ['my-incidents', tableOptions, sort, filteredData, columnFilters],
        getIncidents,
        {
            retry: (failureCount, error) => {
                if (failureCount >= 2) return false;
                if (!error?.response) return true; // Retry if error doesn't have response
                const status = error.response?.status;
                return status >= 500 && status < 600;
            },
            retryDelay: (failureCount) => Math.min(1000 * 2 ** failureCount, 30000),
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 5,
            cacheTime: 1000 * 60 * 10,
            // The QueryClient default is refetchOnMount: false, which kept a
            // combine-invalidated list served from cache on remount.
            refetchOnMount: true,
        }
    );


    if (isSuccess) {
        localStorage?.setItem(
            'next_incidents_ids',
            JSON.stringify(
                incidents?.data?.map((ele) => ({
                    incident_id: ele?.incident_id,
                    user_id: ele?.user_id,
                }))
            )
        );
    }

    const pageCount = Math.ceil(incidents?.total / tableOptions?.rowPerPages);

    // If the list shrank (e.g. incidents combined away in another tab) the
    // current page can now be past the end, which strands the reviewer on an
    // empty page with no pagination controls to click back from. Pull them
    // onto the last page that still exists.
    useEffect(() => {
        if (!isSuccess || !Number.isFinite(pageCount) || pageCount < 1) return;
        if (tableOptions?.activePage > pageCount - 1) {
            setTableOptions((prev) => ({ ...prev, activePage: pageCount - 1 }));
        }
    }, [isSuccess, pageCount, tableOptions?.activePage]);

    const handleOnRowClicked = async (row) => {
        // Save current pagination state with total count to localStorage
        const paginationStateWithTotal = {
            ...tableOptions,
            totalCount: incidents?.total || 0,
            user_id: loggedInUser?.user_id || null
        };
        localStorage.setItem('myIncidentsPaginationState', JSON.stringify(paginationStateWithTotal));
        
        // Store filter data and sort for fetching next pages
        localStorage.setItem('myIncidentsFilterData', JSON.stringify(filteredData));
        if (sort) {
            localStorage.setItem('myIncidentsSort', JSON.stringify(sort));
        }

        navigate(`/myincidents/editmyincident/${row?.incident_id}`, {
            state: {
                ids: incidents?.data?.map((ele) => ({
                    incident_id: ele?.incident_id,
                    user_id: ele?.user_id,
                })),
                paginationState: paginationStateWithTotal,
            },
        });
    };


    return (
        <CustomizedErrorBoundary key={`myincident-parent`}>
            {isError ? (
                <APIErrorComponent error={error} />
            ) : (isLoading || isFetching) ? (
                <RotatingLinesLoader />
            ) : (
                <PaginationAdjustor setTableOptions={setTableOptions}>
                    <>
                        <CustomModuleHeading
                            filteredData={filteredData}
                            pills={{ ...pills, status_exit: 'true' }}
                            handlefilteredData={handlefilteredData}
                            setClearBtn={setClearBtn}
                            filteredLabel={filteredLabel}
                            title={`My Incidents`}
                        >
                            <CustomizedErrorBoundary key={`myincident-filterbutton`}>
                                <div>
                                    <FilterButton
                                        handleOpenModal={handleOpenModal}
                                        clearBtn={clearBtn}
                                        handleClearAllFilteredData={handleClearAllFilteredData}
                                    />
                                </div>
                            </CustomizedErrorBoundary>
                        </CustomModuleHeading>
                        <CustomizedErrorBoundary key={`myincident-filtermodal`}>
                            {isOpen && (
                                <IncidentFilteredModal
                                    show={isOpen}
                                    setClearBtn={setClearBtn}
                                    handlModalClose={handlModalClose}
                                    setIsOpen={setIsOpen}
                                    handlefilteredData={handlefilteredData}
                                    filteredData={filteredData}
                                    isLoadingRefStatus={isLoadingRefStatus}
                                    refStatusOption={refStatusOption}
                                />
                            )}
                        </CustomizedErrorBoundary>
                        <CustomizedErrorBoundary key={`myincident-incidenttable`}>
                            <IncidentTable
                                isLoading={isLoading || isFetching}
                                tableOptions={tableOptions}
                                totaldata={incidents?.total}
                                pageCount={pageCount}
                                data={incidents?.data}
                                userRole={loggedInUser?.role}
                                handlePageClick={handlePageClick}
                                handleRowsPerPage={handleRowsPerPage}
                                handleColumnSort={handleColumnSort}
                                handleOnRowClicked={handleOnRowClicked}
                                filteredData={filteredData}
                                columnFilters={columnFilters}
                                onColumnFilterChange={handleColumnFilterChange}
                                statusOptions={refStatusOption}
                            />
                        </CustomizedErrorBoundary>
                    </>
                </PaginationAdjustor>
            )}
        </CustomizedErrorBoundary>
    );
};

export default MyIncidentsListing;